import { db } from '$db/local/db';
import type { ProblemAttempt, UUID } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso } from './common';

export interface CreateProblemAttemptInput {
  macroSessionId: UUID;
  nodeId: UUID;
  problemNumber: string;
  durationMs: number;
  result: ProblemAttempt['result'];
  errorTaxonomy?: string | null;
  notes?: string | null;
}

export async function createProblemAttempt(input: CreateProblemAttemptInput): Promise<ProblemAttempt> {
  const userId = getActiveUserId();
  const now = nowIso();
  const attempt: ProblemAttempt = {
    id: newId(),
    userId,
    macroSessionId: input.macroSessionId,
    nodeId: input.nodeId,
    problemNumber: input.problemNumber,
    durationMs: input.durationMs,
    result: input.result,
    errorTaxonomy: input.errorTaxonomy,
    notes: input.notes,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };
  await db.problemAttempts.add(attempt);
  return attempt;
}

export async function listAttemptsBySession(sessionId: UUID): Promise<ProblemAttempt[]> {
  const rows = await db.problemAttempts.where('macroSessionId').equals(sessionId).toArray();
  return rows.filter(r => !r.deletedAt);
}

export async function getMaxProblemNumberForNode(nodeId: UUID): Promise<number> {
  const rows = await db.problemAttempts.where('nodeId').equals(nodeId).toArray();
  const active = rows.filter(r => !r.deletedAt);
  let max = 0;
  for (const r of active) {
    const n = parseInt(r.problemNumber || '0', 10);
    if (n > max) max = n;
  }
  return max;
}

export async function listAttemptsByUser(userId = getActiveUserId()): Promise<ProblemAttempt[]> {
  const rows = await db.problemAttempts.where('userId').equals(userId).toArray();
  const activeAttempts = rows.filter(r => !r.deletedAt);

  if (activeAttempts.length === 0) return [];

  // Filtrar intentos cuyas macro-sesiones hayan sido eliminadas
  const sessionIds = [...new Set(activeAttempts.map(a => a.macroSessionId))];
  const sessions = await db.macroSessions.bulkGet(sessionIds);
  const validSessionIds = new Set(
    sessions.filter(s => s && !s.deletedAt).map(s => s!.id)
  );

  return activeAttempts.filter(a => validSessionIds.has(a.macroSessionId));
}

export async function updateProblemAttempt(id: UUID, patch: Partial<ProblemAttempt>): Promise<void> {
  await db.problemAttempts.update(id, { ...patch, updatedAt: nowIso() });
}

export async function softDeleteProblemAttempt(id: UUID): Promise<void> {
  await db.problemAttempts.update(id, { deletedAt: nowIso(), updatedAt: nowIso() });
}